<script>
import { useAlert } from 'dashboard/composables';
import EmojiOrIcon from 'shared/components/EmojiOrIcon.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';

export default {
  components: {
    EmojiOrIcon,
    InlineInput,
  },
  props: {
    href: {
      type: String,
      default: '',
    },
    icon: {
      type: String,
      required: true,
    },
    emoji: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    showCopy: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
  },
  emits: ['update'],
  data() {
    return {
      isEditing: false,
      editValue: '',
    };
  },
  methods: {
    async onCopy(e) {
      e.preventDefault();
      await copyTextToClipboard(this.value);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    },
    startEditing() {
      if (!this.editable) return;
      this.editValue = this.value || '';
      this.isEditing = true;
      this.$nextTick(() => {
        this.$refs.editInput?.focus();
      });
    },
    saveEdit() {
      if (!this.isEditing) return;
      this.isEditing = false;
      const trimmed = this.editValue.trim();
      if (trimmed !== (this.value || '')) {
        this.$emit('update', trimmed);
      }
    },
    cancelEdit() {
      this.isEditing = false;
    },
  },
};
</script>

<template>
  <div class="group/row w-full flex items-center justify-between py-0.5 px-1 rounded hover:bg-slate-100/70 dark:hover:bg-slate-800/50 transition-colors text-xs leading-relaxed">
    <!-- Inline edit mode -->
    <div v-if="isEditing" class="flex items-center gap-1.5 w-full">
      <EmojiOrIcon
        :icon="icon"
        :emoji="emoji"
        icon-size="14"
        class="flex-shrink-0 text-slate-500 dark:text-slate-400"
      />
      <span v-if="title" class="font-medium text-slate-500 dark:text-slate-400 shrink-0">
        {{ title }}:
      </span>
      <InlineInput
        ref="editInput"
        v-model="editValue"
        :placeholder="title"
        class="!w-full text-xs"
        @enter-press="saveEdit"
        @escape-press="cancelEdit"
        @blur="saveEdit"
      />
    </div>

    <!-- Read mode with link -->
    <div v-else-if="href" class="flex items-center justify-between w-full gap-1.5 min-w-0">
      <div class="flex items-center gap-1.5 min-w-0 flex-1">
        <EmojiOrIcon
          :icon="icon"
          :emoji="emoji"
          icon-size="14"
          class="flex-shrink-0 text-slate-500 dark:text-slate-400"
        />
        <span v-if="title" class="font-medium text-slate-600 dark:text-slate-400 shrink-0">
          {{ title }}:
        </span>
        <a
          :href="href"
          target="_blank"
          rel="noopener noreferrer"
          class="text-[#1F93FF] hover:underline truncate font-semibold flex-1"
          :title="value"
        >
          {{ value || $t('CONTACT_PANEL.NOT_AVAILABLE') }}
        </a>
      </div>

      <div class="flex items-center gap-1 shrink-0">
        <button
          v-if="showCopy && value"
          type="button"
          class="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-0.5 rounded transition-colors"
          title="Copiar"
          @click="onCopy"
        >
          📋
        </button>
        <button
          v-if="editable"
          type="button"
          class="opacity-0 group-hover/row:opacity-100 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-0.5 rounded transition-opacity"
          title="Editar"
          @click.prevent="startEditing"
        >
          ✏️
        </button>
      </div>
    </div>

    <!-- Read mode without link -->
    <div v-else class="flex items-center justify-between w-full gap-1.5 min-w-0">
      <div class="flex items-center gap-1.5 min-w-0 flex-1">
        <EmojiOrIcon
          :icon="icon"
          :emoji="emoji"
          icon-size="14"
          class="flex-shrink-0 text-slate-500 dark:text-slate-400"
        />
        <span v-if="title" class="font-medium text-slate-600 dark:text-slate-400 shrink-0">
          {{ title }}:
        </span>
        <span
          v-if="value"
          class="text-slate-800 dark:text-slate-100 font-semibold truncate flex-1 break-words"
          :title="value"
        >
          {{ value }}
        </span>
        <span v-else class="text-slate-400 italic">
          {{ $t('CONTACT_PANEL.NOT_AVAILABLE') }}
        </span>
      </div>

      <div class="flex items-center gap-1 shrink-0">
        <button
          v-if="showCopy && value"
          type="button"
          class="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-0.5 rounded transition-colors"
          title="Copiar"
          @click="onCopy"
        >
          📋
        </button>
        <button
          v-if="editable"
          type="button"
          class="opacity-0 group-hover/row:opacity-100 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-0.5 rounded transition-opacity"
          title="Editar"
          @click="startEditing"
        >
          ✏️
        </button>
      </div>
    </div>
  </div>
</template>
